import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react';
import {useFormik, FormikProvider, FormikErrors} from 'formik';
import {checkEmailIsValid} from '../../_common/utils/checkEmailIsValid';
import {usePageContext} from './PageContext';
import {getPriceQuoteDetails} from '../_redux/actions/priceQuoteDetailsActions';
import {createLink} from '../_redux/actions/popupCreateLinkActions';
import {Customer} from "../../_common/types";

interface PriceQuoteData {
  priceQuote?: {
    customers?: Customer[];
    [key: string]: any;
  };
}

interface FormValues {
  email: string;
  notes: string;
  password: string;
  passwordHint: string;
}

interface ContextType {
  formik: ReturnType<typeof useFormik<FormValues>>;
  handleCreate: () => Promise<void>;
  uniqueEmails: string[];
}

const defaultContext: ContextType = {
  formik: {} as ReturnType<typeof useFormik<FormValues>>,
  handleCreate: async () => {},
  uniqueEmails: []
}

const CreateLinkContext = createContext<ContextType>(defaultContext);

export function useCreateLinkContext() {
  return useContext(CreateLinkContext);
}

export function CreateLinkProvider({children}: { children: ReactNode }) {
  const {priceQuoteID} = usePageContext();
  const [data, setData] = useState<PriceQuoteData | null>(null);

  useEffect(() => {
    if (priceQuoteID && priceQuoteID !== -1) {
      // @ts-ignore
      getPriceQuoteDetails(priceQuoteID).then(setData).catch(console.error);
    }
  }, [priceQuoteID]);

  const customers = data?.priceQuote?.customers ?? [];
  const priceQuoteEmails = Array.isArray(customers)
    ? customers.map(item => item.email).filter(Boolean) as string[]
    : [];
  const uniqueEmails = priceQuoteEmails.filter(
    (item, index) => priceQuoteEmails.indexOf(item) === index
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      email: '',
      notes: '',
      password: '',
      passwordHint: ''
    },
    validate: (values): FormikErrors<FormValues> => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.email) errors.email = 'Email is required';
      else if (!checkEmailIsValid(values.email)) errors.email = 'Invalid email address';

      if (values.password && !values.passwordHint) errors.passwordHint = 'Password hint is required';

      return errors;
    },
    validateOnMount: true,
    onSubmit: () => {
    },
    enableReinitialize: true
  });

  const handleCreate = async () => {
    try {
      await createLink({
        priceQuoteID,
        sentTo: formik.values.email,
        notes: formik.values.notes,
        password: formik.values.password ?? '',
        passwordHint: formik.values.passwordHint
      })
      setTimeout(() => {
        window.location.href = '/price-quotes-page';
      }, 500)
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <CreateLinkContext.Provider value={{...defaultContext, formik, handleCreate, uniqueEmails}}>
      <FormikProvider value={formik}>{children}</FormikProvider>
    </CreateLinkContext.Provider>
  );
}